import { useLazyQuery, useMutation } from '@apollo/client';
import {
  ALL_MODULES,
  ConfirmationModal,
  CustomModal,
  DATE_TIME_FORMAT,
  EventEmitter,
  NewTask,
  actionToast,
  getCurrentUserHook,
  getParsedCrudData,
  getTranslatedText,
  toast,
  useActivityTracker,
  useIsAuthorisedHook,
  useProduct,
  COMMON_CONSTANTS,
  useCheckFeatureGating,
  TICKET_SOURCE
} from '@nile/components';
import {
  AccountClient,
  ADD_WORKLOG_ENTRIES,
  CREATE_TASK,
  GET_STATUS_CONFIGURATION,
  MARKSPAM_TICKETS,
  RESTORE_TICKETS,
  SOFT_TICKET_DELETE,
  GET_TICKET_AUDIT_LIST
} from '@nile/graphql';
import AssociateRunbook from '@nile/mangifera/src/Containers/ToolbarContainers/AssociateRunbook';
import AssociateRunbookSheetNew from '@nile/mangifera/src/Containers/ToolbarContainers/AssociateRunbookSheetNew';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { constructTaskObject } from '@nile/projects/src/Utils';
import {
  Badge,
  Button,
  Modal,
  Popover,
  TextContainer,
  TextStyle,
  Sheet
} from '@superops/ui-design-system';
import React, {
  useCallback,
  useContext,
  useReducer,
  useState,
  useMemo
} from 'react';
import { QRCodeModal } from '@nile/mangifera/src/Components/Ticket';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormattedMessage, injectIntl } from 'react-intl';
import { MergeTicketsFromDetails } from '@nile/mangifera/src/Components/Sheets/MergeTicketSheet';
import ItemsToAdd from '@nile/components/src/Containers/NewWorklog/ItemsToAdd.js';
import { isEmpty } from 'lodash';
import CalendarPage from '@nile/mangifera/src/Pages/SchedulingPage/Pages/CalendarPage.js';
import TicketAssetsSheet from '@nile/mangifera/src/Containers/SheetContainers/TicketAssetsSheet';
import { useGlobalChatHook } from '@nile/components/src/Components/GlobalChatHook';
import { GET_ELIGIBLE_SERVICE_ITEMS } from '@nile/graphql/src/Query/serviceCatalogNew';
import dayjs from 'dayjs';
import { ModuleBasedServiceCatalogModal } from '@nile/client_management/src/Contracts/DeliveryMap/ServiceCatalog/ModuleBasedServiceCatalogModal';
import ApproverListSheet from '@nile/mangifera/src/Containers/SheetContainers/ApproverListSheet.tsx';
import UnspamTicketButton from '@nile/mangifera/src/Components/Ticket/UnspamTicketButton.tsx';
import { useTicketDetailsConversationStore } from '@nile/mangifera/src/StoreManager/store/ticketDetailsConversationStore';

function ActionsToolbar(props) {
  const { intl, hideText } = props;
  const isEditAuthorised = useIsAuthorisedHook('tickets.edit');
  const [popoverActive, setPopoverActive] = useState(false);

  const togglePopoverActive = useCallback(
    () => setPopoverActive((active) => !active),
    []
  );
  const activator = isEditAuthorised ? (
    <Button onClick={togglePopoverActive} icon={<SVGIcon name="Actions" />}>
      {!hideText ? (
        <div className="flex center">
          <div className="ml8">
            <FormattedMessage id="ticket.details.actions" />
          </div>
          <div className="ml8 fx center">
            <SVGIcon name="ArrowDown" />
          </div>
        </div>
      ) : null}
    </Button>
  ) : (
    <span />
  );

  return (
    <div className="vignesh">
      <Popover
        active={popoverActive}
        activator={activator}
        preferredAlignment="right"
        onClose={() => togglePopoverActive}
      >
        <ActionItems
          intl={intl}
          ticket={props.ticket}
          ticketStatus={props.ticketStatus}
          updateSelection={props.updateSelection}
          updateConvertToProject={props.updateConvertToProject}
          togglePopoverActive={togglePopoverActive}
          refetchTicket={props.refetchTicket}
          createTasks={props.createTasks}
          isClassicView={props.isClassicView}
        />
      </Popover>
    </div>
  );
}

function ActionItems(props) {
  const {
    intl,
    ticket,
    togglePopoverActive,
    refetchTicket,
    isClassicView
  } = props;
  const [sheetActive, setSheetActive] = useState(false);
  const [runbookSheetActive, setRunbookSheetActive] = useState(false);
  const [showQRActive, setshowQRActive] = useState(false);
  const [
    associateProductModalActive,
    setAssociateProductModalActive
  ] = useState(false);
  const [schedulingModalActive, setSchedulingModalActive] = useState(false);
  const [showMergeModal, toggleMergeModal] = useReducer((s) => !s, false);
  const [assetSheetActive, setAssetSheetActive] = useState(false);
  const ticketContext = useContext(TicketContext);
  const productType = useProduct();

  const setTicketDescription = useTicketDetailsConversationStore(
    (s) => s.setTicketDescription
  );

  const flags = useFlags();
  const runbookRevampFlag = flags?.unifiedRunbooks;

  const isNewContracts = useCheckFeatureGating('CONTRACT_V3')?.enabled;

  const isAssociateProductAuthorised =
    useIsAuthorisedHook('tickets.edit') &&
    useIsAuthorisedHook('serviceCatalog.view');
  const isAssetViewAuthorised = useIsAuthorisedHook('asset.view');
  const isTicketEditAuthorised = useIsAuthorisedHook('tickets.edit');
  const isProjectAddAuthorised = useIsAuthorisedHook('project.add');

  const TICKET_DESCRIPTION_VARIABLES = useMemo(() => {
    return {
      input: {
        ticket: { ticketId: ticket.ticketId },
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'type',
                operator: 'is',
                value: 'CONVERSATION'
              },
              {
                attribute: 'conversation.type',
                operator: 'is',
                value: 'DESCRIPTION'
              }
            ]
          }
        }
      }
    };
  }, [ticket.ticketId]);

  const [getStatusConfiguration] = useLazyQuery(GET_STATUS_CONFIGURATION);

  const [getTicketDescription] = useLazyQuery(GET_TICKET_AUDIT_LIST, {
    variables: TICKET_DESCRIPTION_VARIABLES
  });

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };

  const [
    initiateApprovalSheetActive,
    setInitiateApprovalSheetActive
  ] = useState(false);

  const toggleInitiateApprovalSheetActive = () => {
    setInitiateApprovalSheetActive(!initiateApprovalSheetActive);
  };

  const openTicketTab = () => {
    if (!ticket) return;

    ticketContext.openTicket({
      module: 'TICKET',
      ticketId: ticket.ticketId,
      data: {
        subject: ticket.subject,
        displayId: ticket.displayId,
        ticketId: ticket.ticketId
      }
    });
  };

  const toggleRunbookSheetActive = () => {
    setRunbookSheetActive(!runbookSheetActive);
  };

  const toggleAssociateProductsModal = () => {
    setAssociateProductModalActive(!associateProductModalActive);
  };

  const toggleSchedulingModal = () => {
    setSchedulingModalActive(!schedulingModalActive);
  };

  const toggleAssetSheet = () => {
    setAssetSheetActive(!assetSheetActive);
  };

  const tabContext = useContext(TabContext);
  const currentUser = getCurrentUserHook();
  const calendarIntegrated = currentUser?.calendarIntegrated;

  const formattedWorkItem = {
    workId: ticket?.ticketId,
    module: ALL_MODULES.ticket,
    client: ticket?.client,
    subject: ticket?.subject,
    displayId: ticket?.displayId,
    requester: ticket?.requester
  };

  const dispatcherTechnicians = [];
  const defaultAttendees = [];

  if (!isEmpty(ticket?.technician) && calendarIntegrated) {
    dispatcherTechnicians.push(ticket?.technician);
    defaultAttendees.push(ticket?.technician);
  }

  if (!isEmpty(ticket?.followers) && calendarIntegrated) {
    ticket?.followers.forEach((el) => {
      dispatcherTechnicians.push(el);
      defaultAttendees.push(el);
    });
  }

  if (!isEmpty(ticket?.requester) && calendarIntegrated) {
    defaultAttendees.push(ticket?.requester);
  }

  const convertToProject = async () => {
    const { data: statusConfigData } = await getStatusConfiguration();
    const closeStateName = statusConfigData?.getStatusConfiguration?.find(
      (config) => config.configuration === 'CLOSE'
    )?.status?.name;
    const { data: ticketDescriptionData } = await getTicketDescription();
    // set ticket description from ticket audit list feed where type is CONVERSATION and conversation.type is DESCRIPTION to show in Project Description for Creation
    const ticketDescription =
      ticketDescriptionData?.getTicketAuditList?.ticketAudit?.[0]?.conversation
        ?.content || '';
    setTicketDescription(ticketDescription);
    if (props.ticketStatus !== closeStateName) {
      props.updateConvertToProject(true);
      props.updateSelection('status', closeStateName);
      sessionStorage.setItem('closeTicketClicked', true);
      return;
    }
    tabContext.openTab({
      module: ALL_MODULES.projectCreation,
      url: `/project/${ticket?.ticketId}/new`,
      data: {
        id: 'ticket-to-project',
        module: 'TICKET',
        displayId: ticket?.displayId,
        ticketId: ticket?.ticketId,
        title: ticket?.subject,
        client: {
          accountId: ticket?.client?.accountId,
          name: ticket?.client?.name
        },
        site: {
          id: ticket?.site?.id,
          name: ticket?.site?.name
        },
        description: ticketDescription
      }
    });
  };

  const addFollowers = () => {
    const newUsers = [
      {
        userId: currentUser.userId,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName
      }
    ];
    const followerObject = getParsedCrudData(
      ticket.followers,
      newUsers,
      'userId'
    );
    const addedFollowers = followerObject.addedItems.map((el) => {
      return {
        userId: el.userId,
        firstName: el.firstName,
        lastName: el.lastName
      };
    });
    props.updateSelection('addFollowers', addedFollowers, refetchTicket);
    togglePopoverActive();
  };
  const deleteFollowers = () => {
    const deletedUsers = [
      {
        userId: currentUser.userId,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName
      }
    ];
    props.updateSelection('deleteFollowers', deletedUsers, refetchTicket);
    togglePopoverActive();
  };

  const associateRunbook = (runbook) => {
    if (runbook) {
      props.updateSelection(
        'sop',
        { sopId: runbook?.sopId, name: runbook.name },
        () => {
          refetchTicket();
          toggleRunbookSheetActive();
          togglePopoverActive();
        }
      );
    }
  };

  const [
    showGlobalTicketConversationWarning,
    setShowGlobalTicketConversationWarning
  ] = useState(false);

  const { getGlobalConversations } = useGlobalChatHook();

  const deleteCurrentTicket = (override) => {
    // Add delete;
    const ticketId = ticket.ticketId;
    const globalConversations = getGlobalConversations() || [];
    const isTicketConversationPresent = globalConversations?.find(
      (el) => el?.id === ticketId
    );

    if (isTicketConversationPresent && !override) {
      setShowGlobalTicketConversationWarning(true);
      return;
    }

    setShowGlobalTicketConversationWarning(false);
    EventEmitter.dispatch('closeGlobalConversation', ticketId);
    deleteTicket(ticketId);
  };

  const viewInNewTab = () => {
    const currentUrl = window.location.href;
    window.open(currentUrl, '_blank');
  };

  const refetchAssets = () => {
    return EventEmitter.dispatch('refetchTicketAssets');
  };

  const getActions = () => {
    const actionItems = [];
    const isEditAuthorised = useIsAuthorisedHook('tickets.edit');
    const isDeleteAuthorised = useIsAuthorisedHook('tickets.delete');
    const isSpamAuthorised = useIsAuthorisedHook('spamTicket.allow');
    const isMergeAuthorised = useIsAuthorisedHook('mergeTicket.allow');

    if (isEditAuthorised) {
      actionItems.push({
        name: 'ADD_TASKS',
        label: getTranslatedText(intl, 'ticket.details.actions.add'),
        icon: <SVGIcon name="Tasks" size="sm" color="#303031" />,
        action: toggleSheetActive,
        group: 'ASSOCIATE',
        index: 4,
        badgeStatus: 'attention'
      });
    }
    if (
      isProjectAddAuthorised &&
      isTicketEditAuthorised &&
      ticket?.workItem?.associations?.length === 0
    ) {
      actionItems.push({
        name: 'ADD_PROJECT',
        label: getTranslatedText(intl, 'ticket.actions.convertToProject'),
        icon: <SVGIcon name="Frequency" size="sm" color="#303031" />,
        action: convertToProject,
        group: 'TICKET_PROPERTY',
        index: 5,
        badgeStatus: 'attention'
      });
    }
    if (
      isMergeAuthorised &&
      !ticket?.parentTicket &&
      ticket?.source !== TICKET_SOURCE.Communication
    ) {
      actionItems.push({
        name: 'MERGE',
        label: getTranslatedText(intl, 'ticket.details.merge.merge'),
        icon: <SVGIcon name="Merge" size="sm" />,
        action: toggleMergeModal,
        group: 'TICKET_PROPERTY',
        index: 1,
        badgeStatus: 'message'
      });
    }
    if (isEditAuthorised) {
      if (
        props.ticket.followers?.length &&
        props.ticket.followers.some((el) => el.userId === currentUser.userId)
      ) {
        actionItems.push({
          name: 'UNFOLLOW',
          label: getTranslatedText(intl, 'ticket.details.actions.unfollow'),
          icon: <SVGIcon name="Follow" size="sm" color="#323232" />,
          action: deleteFollowers,
          group: 'TICKET_PROPERTY',
          index: 2,
          badgeStatus: 'critical'
        });
      } else {
        actionItems.push({
          name: 'FOLLOW',
          label: getTranslatedText(intl, 'ticket.details.actions.follow'),
          icon: <SVGIcon name="Follow" size="sm" color="#323232" />,
          action: addFollowers,
          group: 'TICKET_PROPERTY',
          index: 2,
          badgeStatus: 'success'
        });
      }
    }
    if (isSpamAuthorised) {
      if (ticket?.dormantType === 'SPAM') {
        actionItems.push({
          name: 'UNSPAM',
          label: getTranslatedText(intl, 'common.unspam'),
          icon: <SVGIcon name="Unspam" size="sm" color="#323232" />,
          group: 'TICKET_PROPERTY',
          index: 3,
          badgeStatus: 'attention'
        });
      } else {
        actionItems.push({
          name: 'SPAM',
          label: getTranslatedText(intl, 'ticket.details.actions.spam'),
          icon: <SVGIcon name="Spam" size="sm" color="#323232" />,
          group: 'TICKET_PROPERTY',
          index: 3,
          badgeStatus: 'attention'
        });
      }
    }

    if (flags.qrTagging || productType === COMMON_CONSTANTS.IT) {
      actionItems.push({
        name: 'SHOWQR',
        label: getTranslatedText(intl, 'common.show.qr'),
        icon: <SVGIcon name="QRCode" size="sm" />,
        action: () => setshowQRActive(true),
        group: 'TICKET_PROPERTY',
        index: 4,
        badgeStatus: 'message'
      });
    }
    if (isDeleteAuthorised) {
      actionItems.push({
        name: 'DELETE',
        label: getTranslatedText(intl, 'common.moving.to.trash'),
        icon: (
          <SVGIcon name="Delete" size="sm" color={'var(--sops-delete-color)'} />
        ),
        textVariation: 'negative',
        action: () => deleteCurrentTicket(),
        group: 'TICKET_PROPERTY',
        index: 5,
        badgeStatus: 'critical'
      });
    }

    if (isClassicView) {
      actionItems.push({
        name: 'OPEN_TICKET_TAB',
        label: getTranslatedText(intl, 'ticket.View.newtab'),
        icon: <SVGIcon name="ExternalUrl" size="sm" color="#303031" />,
        action: openTicketTab,
        group: 'TICKET_PROPERTY',
        index: 5,
        badgeStatus: 'secondary'
      });
    }

    if (isAssociateProductAuthorised) {
      actionItems.push({
        name: 'PRODUCT',
        label: getTranslatedText(
          intl,
          'admin.serviceCatalog.quantityType.units'
        ),
        icon: <SVGIcon name="Cart" size="sm" />,
        group: 'ASSOCIATE',
        index: 0,
        badgeStatus: 'warning',
        action: toggleAssociateProductsModal
      });
    }

    if (calendarIntegrated) {
      actionItems.push({
        name: 'SCHEDULE',
        label: getTranslatedText(intl, 'common.schedule.event'),
        icon: <SVGIcon name="DatePicker" size="sm" />,
        group: 'ASSOCIATE',
        index: 1,
        badgeStatus: 'info',
        action: toggleSchedulingModal
      });
    }

    actionItems.push({
      name: 'ASSOCIATE_RUNBOOK',
      label: getTranslatedText(intl, 'admin.runbooks.header'),
      icon: <SVGIcon name="Runbook" size="sm" color="#303031" />,
      action: toggleRunbookSheetActive,
      group: 'ASSOCIATE',
      index: 2,
      badgeStatus: 'secondary'
    });

    if (flags?.unifiedRunbooks) {
      actionItems.push({
        name: 'INITIATE_APPROVAL',
        label: getTranslatedText(
          intl,
          'ticket.details.insights.initiateApprovals'
        ),
        icon: <SVGIcon name="Runbook" size="sm" color="#303031" />,
        action: toggleInitiateApprovalSheetActive,
        group: 'ASSOCIATE',
        index: 3,
        badgeStatus: 'secondary'
      });
    }

    if (isTicketEditAuthorised && isAssetViewAuthorised) {
      actionItems.push({
        name: 'ASSETS',
        label: getTranslatedText(intl, 'common.asset'),
        icon: <SVGIcon name="System" size="sm" />,
        group: 'ASSOCIATE',
        index: 4,
        action: () => toggleAssetSheet()
      });
    }

    return actionItems.sort((a, b) => a?.index - b?.index);
  };
  const actions = getActions();

  const [softDeleteTickets] = useMutation(SOFT_TICKET_DELETE, {
    onCompleted({ softDeleteTickets }) {
      // toast('success', 'Ticket deleted successfully');
    }
  });

  const [restoreTickets] = useMutation(RESTORE_TICKETS, {
    onCompleted({ restoreTickets }) {
      toast('success', 'toast.ticket.restore.success');
    }
  });

  const [markSpamTickets] = useMutation(MARKSPAM_TICKETS, {
    onCompleted({ markSpamTickets }) {
      toast('success', 'ticket.details.actions.spamTicket.success');
    }
  });

  const [createTask] = useMutation(CREATE_TASK, {
    onCompleted() {
      toast('success', 'ticket.details.actions.add.taskAssociate.success');
      toggleSheetActive();
      EventEmitter.dispatch('taskAddition');
      refetchTicket();
      useActivityTracker({
        event: `Ticket - Task created successfully`
      });
      togglePopoverActive();
    },
    onError() {
      // toast('error', 'Something went wrong');
    }
  });

  const handleTaskAddition = (type, taskList) => {
    const taskObj = constructTaskObject(taskList);
    createTask({
      variables: { ...taskObj, ticket: { ticketId: ticket.ticketId } },
      input: { ...taskObj, ticket: { ticketId: ticket.ticketId } }
    });
  };

  const restoreTicket = (id) => {
    restoreTickets({ variables: { ids: [id] } });
  };

  const deleteTicket = (id) => {
    softDeleteTickets({
      variables: {
        ids: [id]
      }
    }).then((res) => {
      if (!isClassicView) {
        tabContext.closeTab();
      }
      actionToast('info', 'toast.ticket.softdelete.success', {
        title: 'ticket.view.undo.button',
        action: () => {
          restoreTicket(id);
          tabContext.openTab({
            ticketId: ticket.ticketId,
            module: 'TICKET',
            data: {
              subject: ticket.subject,
              displayId: ticket.displayId,
              ticketId: ticket.ticketId
            }
          });
        }
      });
      EventEmitter.dispatch('currentTicketDeleted', id);
    });
    togglePopoverActive();
  };

  const spamTicket = (id) => {
    markSpamTickets({
      variables: {
        ids: [id],
        markSpamRequester: true
      }
    }).then((res) => {
      EventEmitter.dispatch('currentTicketSpammed', id);
    });
    togglePopoverActive();
  };

  const closeQRModal = () => {
    setshowQRActive(false);
  };

  const ActionContent = (props) => {
    const action = props?.action;
    return (
      <div
        onClick={action?.action && action.action}
        className="flex center g8 pointer actr p5 hover-bg br-5"
      >
        <div className="flex">
          <Badge status={action.badgeStatus} icon={action.icon} radial />
        </div>
        <div id={`action-text-${action?.label}`}>
          <TextStyle variation={action?.textVariation}>
            {action.label}
          </TextStyle>
        </div>
      </div>
    );
  };

  const getActionContent = (action) => {
    switch (action.name) {
      case 'SPAM':
        return (
          <ConfirmationModal
            modalActivator={<ActionContent action={action} />}
            description={getTranslatedText(
              intl,
              'ticket.details.actions.spam.confirm'
            )}
            handleConfirmation={() => spamTicket(ticket.ticketId)}
            title={getTranslatedText(intl, 'ticket.details.actions.spam.text')}
          />
        );
      case 'UNSPAM':
        return (
          <UnspamTicketButton
            inputVariables={{
              ticket: [{ ticketId: ticket.ticketId }],
              unspamRequester: true
            }}
            onCompleted={() => {
              EventEmitter.dispatch('refetchTicketFromAI');
              togglePopoverActive();
            }}
            showWarning
            type="ActionList"
          />
        );
      default:
        return <ActionContent action={action} />;
    }
  };

  const GROUPS = [
    {
      name: 'ASSOCIATE',
      label: getTranslatedText(intl, 'ticket.details.tasks.create')
    },
    {
      name: 'TICKET_PROPERTY',
      label: getTranslatedText(intl, 'admin.eventTrigger.property')
    }
  ];

  return (
    <div className="flex col g10 p10">
      <>
        {GROUPS.map((group) => {
          const groupActions = actions.filter(
            (action) => action?.group === group?.name
          );
          return (
            <div id="group-wrapper">
              <TextStyle variation="subdued" uppercase>
                <div className="mb5">{group?.label}</div>
              </TextStyle>

              {groupActions.map((action) => (
                <div>{getActionContent(action)}</div>
              ))}
            </div>
          );
        })}
      </>

      {showQRActive && (
        <QRCodeModal
          id={ticket?.ticketId}
          displayId={ticket?.displayId}
          clientName={ticket?.client?.name}
          siteName={ticket?.site?.name}
          type={'ticket'}
          showQRActive={showQRActive}
          closeQRModal={closeQRModal}
        />
      )}

      {sheetActive && (
        <NewTask
          addOrUpdateTechGroup={handleTaskAddition}
          state={sheetActive}
          toggleSheetActive={toggleSheetActive}
          name="Add Task"
          hideIcon
          isLink
          noRender
          module="TICKET_TASK"
          details={
            props?.data?.taskId
              ? {
                  operation: 'UPDATE',
                  projectId: props?.project?.projectId,
                  taskId: props?.data?.taskId
                }
              : props?.project?.projectId
              ? {
                  operation: 'CREATE',
                  projectId: props?.project?.projectId
                }
              : {
                  operation: 'CREATE',
                  ticketId: ticket?.ticketId
                }
          }
          locked
        />
      )}

      {runbookSheetActive &&
        (runbookRevampFlag ? (
          <AssociateRunbookSheetNew
            state={runbookSheetActive}
            toggleSheetActive={toggleRunbookSheetActive}
            ticket={ticket}
            associateRunbook={associateRunbook}
          />
        ) : (
          <AssociateRunbook
            state={runbookSheetActive}
            toggleSheetActive={toggleRunbookSheetActive}
            ticket={ticket}
            associateRunbook={associateRunbook}
          />
        ))}

      {showMergeModal && (
        <MergeTicketsFromDetails
          toggleModal={toggleMergeModal}
          activeTicket={ticket}
          refetchTicket={refetchTicket}
        />
      )}

      {associateProductModalActive &&
        (isNewContracts ? (
          <ModuleBasedServiceCatalogModal
            closeModal={toggleAssociateProductsModal}
            ticket={ticket}
            open={associateProductModalActive}
          />
        ) : (
          <CustomModal
            open={associateProductModalActive}
            onClose={toggleAssociateProductsModal}
            locked
          >
            <ItemsToAdd
              closeModal={toggleAssociateProductsModal}
              client={ticket?.client}
              ticket={ticket}
              module={ALL_MODULES.ticket}
              locked
            />
          </CustomModal>
        ))}

      {schedulingModalActive && (
        <CustomModal
          open={schedulingModalActive}
          onClose={() => {
            setSchedulingModalActive(false);
          }}
          zIndex={500}
          className="ticket-scheduling-modal"
          locked
        >
          <div className="ticket-scheduling-wrapper">
            <CalendarPage
              workItem={formattedWorkItem}
              dispatcherTechnicians={dispatcherTechnicians}
              attendeesFromWorkItem={defaultAttendees}
              onClose={() => {
                setSchedulingModalActive(false);
              }}
              hideCalendarSettings
              mixPanelEventSource={ALL_MODULES.ticket}
            />
          </div>
        </CustomModal>
      )}

      {assetSheetActive && (
        <TicketAssetsSheet
          toggleSheetActive={toggleAssetSheet}
          refetchAssets={refetchAssets}
          ticket={ticket}
          open={assetSheetActive}
        />
      )}

      {showGlobalTicketConversationWarning && (
        <Modal
          activator={<></>}
          open={showGlobalTicketConversationWarning}
          onClose={() => {
            setShowGlobalTicketConversationWarning(false);
          }}
          variant="confirmation"
          title={<FormattedMessage id="app.general.toast.warning" />}
          primaryAction={{
            content: <FormattedMessage id="common.close" />,
            onAction: () => {
              deleteCurrentTicket(true);
            },
            destructive: true
          }}
          iconSize="xl"
          secondaryActions={[
            {
              content: <FormattedMessage id="common.cancel" />,
              onAction: () => {
                setShowGlobalTicketConversationWarning(false);
              }
            }
          ]}
        >
          <TextContainer>
            <FormattedMessage id="ticket.global.conversation.close.warning" />
          </TextContainer>
        </Modal>
      )}

      {initiateApprovalSheetActive && (
        <Sheet
          open={initiateApprovalSheetActive}
          onClose={toggleInitiateApprovalSheetActive}
        >
          <ApproverListSheet
            ticketId={ticket?.ticketId}
            client={ticket?.client}
            onSave={() => {
              toggleInitiateApprovalSheetActive();
              EventEmitter.dispatch('refetchTicketApproval');
              EventEmitter.dispatch('approvalChange');
            }}
            onCancel={toggleInitiateApprovalSheetActive}
          />
        </Sheet>
      )}
    </div>
  );
}

export default injectIntl(ActionsToolbar);
